import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Eye, EyeOff, RefreshCw, KeyRound, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { fmtBytes, fmtNum } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ingest`;
const URL_UPLOAD = `${API}/upload`;

const copy = (t, what) => navigator.clipboard.writeText(t).then(() => toast.success(`${what} copied`));
const btn = "inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800";

const Code = ({ text, copyText, testId, label }) => (
  <div className="relative">
    <pre className="rounded-lg bg-[#0B0F19] border border-slate-800 p-3 pr-24 text-[11px] font-mono text-slate-300 overflow-x-auto whitespace-pre" data-testid={testId}>{text}</pre>
    <button onClick={() => copy(copyText || text, label)} className={`${btn} absolute top-2 right-2`}><Copy className="h-3 w-3" /> Copy</button>
  </div>
);

const Status = ({ s }) => {
  if (s === "success") return <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> Success</span>;
  if (s === "failed") return <span className="inline-flex items-center gap-1 text-rose-400"><XCircle className="h-3.5 w-3.5" /> Failed</span>;
  return <span className="inline-flex items-center gap-1 text-amber-300"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {s}</span>;
};

export const AutoImport = ({ onImported }) => {
  const [key, setKey] = useState("");
  const [show, setShow] = useState(false);
  const [runs, setRuns] = useState([]);
  const load = useCallback(() => axios.get(`${API}/runs`).then((r) => setRuns(r.data)).catch(() => {}), []);
  useEffect(() => { axios.get(`${API}/key`).then((r) => setKey(r.data.key)); load(); }, [load]);
  useEffect(() => {
    if (!runs.some((r) => r.status === "received" || r.status === "processing")) return;
    const t = setTimeout(() => load().then(() => onImported?.()), 4000);
    return () => clearTimeout(t);
  }, [runs, load, onImported]);
  const rotate = async () => {
    if (!window.confirm("Create a new key? The old key stops working immediately, so update your server script.")) return;
    const r = await axios.post(`${API}/key/rotate`);
    setKey(r.data.key); setShow(true); toast.success("New key created");
  };
  const script = (k) => ({
    ps: `# wam-upload.ps1 - sends the daily export to Warehouse Activity Monitor
$key  = "${k}"
$file = "D:\\Exports\\User_Performance_12Months.zip"   # change to your export path
curl.exe --fail --silent --show-error -X POST "${URL_UPLOAD}" \`
  -H "X-API-Key: $key" -F "file=@$file"`,
    linux: `30 6 * * * curl --fail -sS -X POST "${URL_UPLOAD}" -H "X-API-Key: ${k}" -F "file=@/data/exports/User_Performance_12Months.zip"`,
  });
  const real = script(key), shown = script(show ? key : "<KEY - click the eye to reveal>");
  const task = `schtasks /create /tn "WAM daily upload" /sc daily /st 06:30 /ru SYSTEM /tr "powershell -NoProfile -ExecutionPolicy Bypass -File C:\\Scripts\\wam-upload.ps1"`;
  return (
    <div className="panel p-6 space-y-6" data-testid="auto-import">
      <div>
        <h2 className="font-display text-xl font-semibold text-slate-100">Automatic daily import</h2>
        <p className="text-sm text-slate-400 mt-1 max-w-3xl">Your server sends the daily zip (or CSV) to the link below. The app reads it on the server and <b className="text-slate-200">replaces</b> the current data, usually within 15–30 seconds. Notes, shifts, hidden pickers and holidays are kept. If a file looks incomplete (under 10% of the current rows) it is rejected and the old data stays.</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-1.5">
          <p className="label-mono">Upload link (POST, form field "file")</p>
          <div className="flex gap-2 items-center"><code className="flex-1 truncate rounded-md bg-[#0B0F19] border border-slate-800 px-3 py-2 text-xs font-mono text-slate-200" data-testid="ingest-url">{URL_UPLOAD}</code><button onClick={() => copy(URL_UPLOAD, "Link")} className={btn}><Copy className="h-3 w-3" /></button></div>
        </div>
        <div className="space-y-1.5">
          <p className="label-mono">Secret key (header X-API-Key)</p>
          <div className="flex gap-2 items-center">
            <code className="flex-1 truncate rounded-md bg-[#0B0F19] border border-slate-800 px-3 py-2 text-xs font-mono text-slate-200" data-testid="ingest-key">{show ? key : "•".repeat(32)}</code>
            <button data-testid="ingest-key-toggle" onClick={() => setShow(!show)} className={btn}>{show ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}</button>
            <button onClick={() => copy(key, "Key")} className={btn}><Copy className="h-3 w-3" /></button>
            <button data-testid="ingest-key-rotate" onClick={rotate} className={btn} title="Create a new key"><KeyRound className="h-3 w-3" /> New</button>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <p className="label-mono">Windows server: 1) save as C:\Scripts\wam-upload.ps1</p>
        <Code text={shown.ps} copyText={real.ps} testId="ingest-ps-script" label="Script" />
        <p className="label-mono">2) schedule it daily at 06:30 (run once in an admin prompt)</p>
        <Code text={task} testId="ingest-task-cmd" label="Command" />
        <p className="label-mono">Linux server alternative (crontab -e)</p>
        <Code text={shown.linux} copyText={real.linux} testId="ingest-cron-line" label="Cron line" />
        <p className="text-[11px] text-slate-500">Keep the key private, as anyone with it can replace the data. Server time zone is used for 06:30.</p>
      </div>
      <div>
        <div className="flex items-center mb-2"><p className="label-mono mr-auto">Recent imports</p><button data-testid="ingest-runs-refresh" onClick={load} className={btn}><RefreshCw className="h-3 w-3" /> Refresh</button></div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="ingest-runs-table">
            <thead><tr className="text-slate-500 border-b border-slate-800">{["Received", "File", "Size", "Status", "Rows", "Users", "Months", "Time", "Details"].map((h) => <th key={h} className="text-left py-2 pr-4 font-mono uppercase tracking-wider text-[10px]">{h}</th>)}</tr></thead>
            <tbody>
              {runs.length === 0 && <tr><td colSpan={9} className="py-4 text-slate-500">No automatic imports yet.</td></tr>}
              {runs.map((r) => (
                <tr key={r.runId} className="border-b border-slate-800/60" data-testid={`ingest-run-${r.runId}`}>
                  <td className="py-2 pr-4 font-mono text-slate-300 whitespace-nowrap">{new Date(r.receivedAt).toLocaleString("en-GB")}</td>
                  <td className="pr-4 text-slate-300">{r.csvName || r.fileName}</td>
                  <td className="pr-4 font-mono text-slate-400">{fmtBytes(r.uploadSize || 0)}</td>
                  <td className="pr-4 whitespace-nowrap"><Status s={r.status} /></td>
                  <td className="pr-4 font-mono text-slate-300">{r.rowsRead ? fmtNum(r.rowsRead) : "—"}</td>
                  <td className="pr-4 font-mono text-slate-300">{r.users ?? "—"}</td>
                  <td className="pr-4 font-mono text-slate-400 whitespace-nowrap">{r.months ? `${r.months[0]} → ${r.months[1]}` : "—"}</td>
                  <td className="pr-4 font-mono text-slate-400">{r.seconds ? `${r.seconds}s` : "—"}</td>
                  <td className="text-rose-300 max-w-xs">{r.error || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
